import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { BehaviorSubject, combineLatest, map, shareReplay } from 'rxjs';
import { Employment } from 'server/shared/models/employment';
import { EmploymentInviteeStatusEnum } from 'server/shared/models/employmentInviteeStatusEnum';
import { canAccessEmploymentInvitee } from '../../employments/employment-edit/canAccessEmploymentInvitee';
import { CustomerService } from '../../services/customer.service';

type EmploymentCounts = {
  open: number;
  available: number;
  unavailable: number;
  booked: number;
};

@Component({
  selector: 'app-employment-counts',
  templateUrl: './employment-counts.component.html',
  styleUrls: ['./employment-counts.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmploymentCountsComponent {
  public employment$ = new BehaviorSubject<Employment | undefined>(undefined);

  counts$ = combineLatest([
    this.employment$,
    this.customerService.readyDispoCustomer$,
    this.customerService.sharedPoolCustomers$,
  ]).pipe(
    map(([employment, currentCustomer, sharedPoolCustomers]) => {
      if (!employment || !currentCustomer) {
        return {
          open: 0,
          available: 0,
          unavailable: 0,
          booked: 0,
        };
      }
      const filteredInvitees = employment.invitees.filter((invitee) =>
        canAccessEmploymentInvitee(
          invitee,
          currentCustomer,
          employment,
          sharedPoolCustomers,
        ),
      );
      const open = filteredInvitees.filter(
        (i) => i.status === EmploymentInviteeStatusEnum.OPEN,
      ).length;
      const available = filteredInvitees.filter(
        (i) =>
          i.status === EmploymentInviteeStatusEnum.AVAILABLE ||
          i.status === EmploymentInviteeStatusEnum.POSSIBLE_CANDIDATE,
      ).length;
      const unavailable = filteredInvitees.filter((i) =>
        [
          EmploymentInviteeStatusEnum.UNAVAILABLE,
          EmploymentInviteeStatusEnum.DECLINED,
          EmploymentInviteeStatusEnum.CANCELLED,
        ].includes(i.status),
      ).length;
      const booked = filteredInvitees.filter(
        (i) => i.status === EmploymentInviteeStatusEnum.BOOKED,
      ).length;
      return {
        open,
        available,
        unavailable,
        booked,
      };
    }),
    shareReplay(1),
  );

  @Input() set employment(employment: Employment | undefined) {
    this.employment$.next(employment);
  }

  constructor(private customerService: CustomerService) {}

  readonly employmentInviteeStatusEnum = EmploymentInviteeStatusEnum;
}
